import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { formatCurrency } from '../data/model';
import type { DemoSnapshot } from '../data/types';

export function QuotesPage(props: {
  quoteIds: string[];
  selectedQuoteId: string;
  quoteTitle: string;
  quoteClient: string;
  quoteSite: string;
  activeQuoteLines: DemoSnapshot['activeQuoteLines'];
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  quoteHasProbeCheck: boolean;
  quoteConverted: boolean;
  quoteSignals: DemoSnapshot['quoteSignals'];
  onSelectQuote: (quoteId: string) => void;
  onAddProbeCheck: () => Promise<void>;
  onConvertToWorkOrder: () => Promise<void>;
  onSaveQuoteLine: (
    quoteId: string,
    lineId: string,
    payload: { label: string; qtyLabel: string; quantity: number; unitCents: number },
  ) => Promise<void>;
  quoteLineEditingEnabled: boolean;
  onOpenQuoteList?: () => void;
  canManageQuotes: boolean;
}) {
  const [showQuoteList, setShowQuoteList] = useState(false);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [draftLines, setDraftLines] = useState(props.activeQuoteLines);
  const [savingLineId, setSavingLineId] = useState<string | null>(null);

  useEffect(() => {
    setDraftLines(props.activeQuoteLines);
    setActiveLineId(props.activeQuoteLines[0]?.id ?? null);
  }, [props.selectedQuoteId, props.activeQuoteLines]);

  async function persistLine(lineId: string) {
    if (!props.quoteLineEditingEnabled || !props.canManageQuotes) return;
    const line = draftLines.find((candidate) => candidate.id === lineId);
    const originalLine = props.activeQuoteLines.find((candidate) => candidate.id === lineId);
    if (!line || !originalLine) return;
    if (
      line.label === originalLine.label &&
      line.qtyLabel === originalLine.qtyLabel &&
      line.quantity === originalLine.quantity &&
      line.unitCents === originalLine.unitCents
    ) {
      return;
    }

    setSavingLineId(lineId);
    try {
      await props.onSaveQuoteLine(props.selectedQuoteId, lineId, {
        label: line.label,
        qtyLabel: line.qtyLabel,
        quantity: line.quantity,
        unitCents: line.unitCents,
      });
    } catch {
      // The app shell already surfaces the failure; keep the editor responsive.
    } finally {
      setSavingLineId(null);
    }
  }

  const draftSubtotalCents = useMemo(
    () => draftLines.reduce((sum, line) => sum + line.quantity * line.unitCents, 0),
    [draftLines],
  );
  const draftVatCents = useMemo(() => Math.round(draftSubtotalCents * 0.2), [draftSubtotalCents]);
  const draftTotalCents = useMemo(() => draftSubtotalCents + draftVatCents, [draftSubtotalCents, draftVatCents]);

  const suggestionNotes = [
    props.quoteHasProbeCheck
      ? {
          text: 'Ligne "contrôle sonde extérieure" ajoutée au devis et prise en compte dans le total.',
          applied: true,
        }
      : {
          text: 'Ajouter une ligne "contrôle sonde extérieure" car le site remonte déjà deux incidents hivernaux.',
          applied: false,
        },
    {
      text: 'Proposer un contrat trimestriel "maintenance PAC critique" pour réduire les délais de rediagnostic.',
      applied: false,
    },
    {
      text: 'Alerte marge: rester sur un déplacement unique, ne pas sous-estimer le temps de remise en service.',
      applied: false,
    },
  ];

  return (
    <div className="page-stack">
      <section className="page-intro page-intro--quote">
        <div>
          <div className="page-intro__crumbs">
            <button
              type="button"
              className="page-intro__crumb"
              onClick={() => {
                setShowQuoteList(true);
                props.onOpenQuoteList?.();
              }}
            >
              Devis
            </button>
            <span className="material-symbols-outlined">chevron_right</span>
            <span>Assistant IA</span>
          </div>
          <h2>Brouillon de devis - {props.quoteClient}</h2>
          <p className="page-intro__location">
            <span className="material-symbols-outlined">location_on</span>
            <span>{props.quoteSite}</span>
          </p>
        </div>
        <div className="page-intro__actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => void props.onAddProbeCheck()}
            disabled={!props.canManageQuotes || props.quoteHasProbeCheck}
          >
            <span className="material-symbols-outlined">auto_awesome</span>
            <span>{props.quoteHasProbeCheck ? 'Suggestion ajoutée' : 'Ajouter une suggestion'}</span>
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => void props.onConvertToWorkOrder()}
            disabled={!props.canManageQuotes}
          >
            <span className="material-symbols-outlined">rocket_launch</span>
            <span>{props.quoteConverted ? 'Intervention créée' : 'Convertir en intervention'}</span>
          </button>
        </div>
      </section>

      {showQuoteList && props.quoteIds.length > 1 ? (
        <Card
          title="Liste des devis"
          subtitle="Sélectionnez un devis pour revenir au brouillon et éditer les lignes."
          actions={
            <button type="button" className="secondary-button" onClick={() => setShowQuoteList(false)}>
              Revenir au détail
            </button>
          }
        >
          <div className="quote-list-grid">
            {props.quoteIds.map((quoteId) => (
              <button
                key={quoteId}
                type="button"
                className={`quote-list-card${quoteId === props.selectedQuoteId ? ' is-active' : ''}`}
                onClick={() => {
                  props.onSelectQuote(quoteId);
                  setShowQuoteList(false);
                }}
              >
                <strong>{quoteId}</strong>
                <span>{quoteId === props.selectedQuoteId ? props.quoteTitle : 'Ouvrir le devis'}</span>
                <em>{quoteId === props.selectedQuoteId ? props.quoteClient : 'Sélection rapide'}</em>
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      <div className="quote-layout">
        <Card
          title={`Brouillon de devis · ${props.selectedQuoteId}`}
          subtitle={`${props.quoteTitle} · ${props.quoteClient} · ${props.quoteSite}`}
          actions={
            <button
              type="button"
              className="quote-table__action"
              onClick={() => void props.onAddProbeCheck()}
              disabled={!props.canManageQuotes || props.quoteHasProbeCheck}
            >
              <span className="material-symbols-outlined">add_circle</span>
              <span>{props.quoteHasProbeCheck ? 'Suggestion active' : 'Ajouter une ligne IA'}</span>
            </button>
          }
        >
          {props.quoteIds.length > 1 ? (
            <div className="billing-toolbar">
              <label className="field-stack">
                <span>Devis</span>
                <select value={props.selectedQuoteId} onChange={(event) => props.onSelectQuote(event.target.value)}>
                  {props.quoteIds.map((quoteId) => (
                    <option key={quoteId} value={quoteId}>
                      {quoteId}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ) : null}
          <div className="settings-updated-at">
            {props.quoteLineEditingEnabled
              ? 'Les lignes se sauvegardent automatiquement dès que vous quittez un champ.'
              : "L'édition persistante des lignes est disponible en mode connecté."}
          </div>
          <div className="quote-table">
            <div className="quote-table__head">
              <span>Libellé</span>
              <span>Qté</span>
              <span>PU HT</span>
              <span>Total</span>
            </div>
            {draftLines.map((line) => (
              <div
                key={line.id}
                className={`quote-table__row quote-table__row--interactive${activeLineId === line.id ? ' is-active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveLineId(line.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveLineId(line.id);
                  }
                }}
              >
                <input
                  className="quote-table__input quote-table__input--label"
                  value={line.label}
                  disabled={!props.canManageQuotes || !props.quoteLineEditingEnabled}
                  onChange={(event) =>
                    setDraftLines((current) =>
                      current.map((candidate) =>
                        candidate.id === line.id ? { ...candidate, label: event.target.value } : candidate,
                      ),
                    )
                  }
                  onBlur={() => void persistLine(line.id)}
                />
                <input
                  className="quote-table__input quote-table__input--qty"
                  value={line.qtyLabel}
                  disabled={!props.canManageQuotes || !props.quoteLineEditingEnabled}
                  onChange={(event) =>
                    setDraftLines((current) =>
                      current.map((candidate) =>
                        candidate.id === line.id ? { ...candidate, qtyLabel: event.target.value } : candidate,
                      ),
                    )
                  }
                  onBlur={() => void persistLine(line.id)}
                />
                <input
                  className="quote-table__input quote-table__input--unit"
                  value={formatCurrency(line.unitCents)}
                  disabled={!props.canManageQuotes || !props.quoteLineEditingEnabled}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/\s+/g, '').replace('€', '').replace(',', '.');
                    const parsed = Number.parseFloat(raw);
                    if (!Number.isFinite(parsed)) return;
                    setDraftLines((current) =>
                      current.map((candidate) =>
                        candidate.id === line.id ? { ...candidate, unitCents: Math.round(parsed * 100) } : candidate,
                      ),
                    );
                  }}
                  onBlur={() => void persistLine(line.id)}
                />
                <strong>
                  {savingLineId === line.id ? 'Enregistrement…' : formatCurrency(line.quantity * line.unitCents)}
                </strong>
              </div>
            ))}
          </div>

          <div className="quote-summary">
            <div>
              <span>Sous-total</span>
              <strong>{formatCurrency(draftSubtotalCents)}</strong>
            </div>
            <div>
              <span>TVA</span>
              <strong>{formatCurrency(draftVatCents)}</strong>
            </div>
            <div className="quote-summary__total">
              <span>Total TTC</span>
              <strong>{formatCurrency(draftTotalCents)}</strong>
            </div>
          </div>
        </Card>

        <Card title="Guidage IA" subtitle="Stratégie opérationnelle et vente additionnelle">
          <div className="ai-stack">
            <div className="quote-signal-grid">
              {props.quoteSignals.map((item) => (
                <div key={item.label} className="quote-signal">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                  <span>{item.detail}</span>
                </div>
              ))}
            </div>
            <div className="ai-note">
              Sources IA visibles: historique Clinique Valmy 2025, catalogue PAC critique, référentiel marge service.
            </div>
            {suggestionNotes.map((item) => (
              <div key={item.text} className={`ai-note${item.applied ? ' ai-note--applied' : ''}`}>
                {item.text}
              </div>
            ))}
            <div className="quote-upsell-card">
              <div>
                <span className="quote-upsell-card__kicker">Vente additionnelle recommandée</span>
                <strong>Maintenance PAC Annuelle</strong>
                <p>Le kit nettoyage est à 50% si couplé avec un abonnement de maintenance.</p>
              </div>
              <button
                type="button"
                className="primary-button primary-button--compact"
                onClick={() => void props.onAddProbeCheck()}
                disabled={!props.canManageQuotes || props.quoteHasProbeCheck}
              >
                <span className="material-symbols-outlined">add</span>
                <span>Ajouter la suggestion IA</span>
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
